import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * The catalog list's keys: `/` to the filter, `↑` `↓` to move, `↵` to open.
 *
 * Listened for on the document rather than on the list, because the point of
 * them is that the reader does not have to put the focus anywhere first: they
 * land on the page, press `/`, type, press `↓` twice and `↵`.
 *
 * What the keys leave alone matters as much as what they do:
 * - any other field — a key typed into a text box is text;
 * - a dialog or a menu, which has its own arrows and its own Enter — the
 *   search dialog and the command palette open above this page;
 * - Enter on a link or a button, which already means "press this";
 * - anything with a modifier held, which belongs to the browser.
 */

const EDITABLE = ['INPUT', 'TEXTAREA', 'SELECT'];

/**
 * Whether a key pressed at `target` is a menu's or a dialog's to handle. Asked
 * of the key's own target rather than of the document, so something that
 * stays mounted elsewhere with one of these roles cannot switch the list's
 * keys off for good.
 *
 * Three ways a key reaches a popup, and each is one of these:
 * - the focus is inside it;
 * - it is modal and the focus has not reached it yet — a modal hides the rest
 *   of the page from assistive technology while it is open, the list with it;
 * - the focus is still on the control that opened it, as it is on the shell's
 *   own menus, which are not modal.
 */
const belongsToAPopup = (target: HTMLElement) =>
  !!target.closest(
    '[role="dialog"], [role="menu"], [role="listbox"], [aria-hidden="true"], [aria-haspopup][aria-expanded="true"]',
  );

export function useListKeyboard({
  count,
  filterRef,
  onOpen,
  text,
  scope,
}: {
  /** How many rows are shown. */
  count: number;
  filterRef: RefObject<HTMLInputElement>;
  onOpen: (index: number) => void;
  /** The filter's text: typing puts the first match under the cursor. */
  text: string;
  /** Anything else that replaces the rows: a new tab, chip or filter. */
  scope: string;
}): number {
  const [active, setActive] = useState(-1);
  const activeRef = useRef(active);
  activeRef.current = active;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const countRef = useRef(count);
  countRef.current = count;

  // The cursor follows the rows in layout effects, not passive ones. A passive
  // effect's update renders in a later task, and a browser hands queued key
  // presses out before that task: `↵` typed right behind the last letter then
  // found nothing under the cursor yet and was dropped. A layout effect's
  // update renders before the browser takes the next key.

  // A different list: nothing is under the cursor until the reader moves.
  useLayoutEffect(() => setActive(-1), [scope]);

  // Typing: the best match is under the cursor, so ↵ opens it.
  useLayoutEffect(() => {
    setActive(text.trim() && countRef.current > 0 ? 0 : -1);
  }, [text]);

  // Fewer rows than before: the cursor stays on the list.
  useLayoutEffect(() => {
    setActive(current => (current >= count ? count - 1 : current));
  }, [count]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target && belongsToAPopup(target)) return;
      const inFilter = !!target && target === filterRef.current;
      const editable =
        !!target &&
        (target.isContentEditable || EDITABLE.includes(target.tagName));

      if (e.key === '/') {
        if (editable) return;
        e.preventDefault();
        filterRef.current?.focus();
        filterRef.current?.select();
        return;
      }
      if (editable && !inFilter) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const rows = countRef.current;
        if (!rows) return;
        e.preventDefault();
        const down = e.key === 'ArrowDown';
        setActive(current => {
          if (current < 0) return 0;
          return down
            ? Math.min(rows - 1, current + 1)
            : Math.max(0, current - 1);
        });
        return;
      }

      if (e.key === 'Enter') {
        if (activeRef.current < 0) return;
        if (
          !inFilter &&
          target?.closest('a, button, [role="button"], [role="tab"]')
        ) {
          return;
        }
        e.preventDefault();
        onOpenRef.current(activeRef.current);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [filterRef]);

  return active;
}
